import React, { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Shield, Building2, AlertCircle } from 'lucide-react';
import { isSafeReturnPath, safeReturnPath } from '../../lib/safePath';

export default function LoginPage() {
  const { loginWithRedirect, isLoading, error, isAuthenticated } = useAuth0();
  const [authError, setAuthError] = useState<string | null>(null);

  // If Auth0 returns an error in the URL, show it
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const err = params.get('error');
    const errDesc = params.get('error_description');
    if (err) {
      setAuthError(errDesc || err);
      // Clean up the URL
      window.history.replaceState({}, document.title, '/login');
    }
  }, []);

  useEffect(() => {
    if (error) {
      setAuthError(error.message);
    }
  }, [error]);

  // If already authenticated, redirect to dashboard — UNLESS we were just
  // force-logged-out because the backend rejected the token (an expired access
  // token while the Auth0 session cookie still says "authenticated"). In that
  // case, auto-bouncing would ping-pong /login ↔ /dashboard, so we stay here and
  // show the sign-in screen so the user can re-authenticate. The flag is set by
  // the API interceptor (lib/api.ts) and auto-expires after 30s.
  useEffect(() => {
    if (!isAuthenticated) return;
    const forced = sessionStorage.getItem('previsit_forced_logout');
    if (forced && Date.now() - Number(forced) < 30000) return; // recently kicked out → don't bounce
    if (forced) sessionStorage.removeItem('previsit_forced_logout'); // stale flag → clean up
    // Honor a stashed deep link (an already-authenticated doctor routed through /login from an
    // email link) instead of always forcing /dashboard.
    let dest = '/dashboard';
    try {
      const rt = sessionStorage.getItem('previsit_return_to');
      if (rt) { sessionStorage.removeItem('previsit_return_to'); dest = safeReturnPath(rt); } // reject open-redirect paths
    } catch { /* ignore */ }
    window.location.href = dest;
  }, [isAuthenticated]);

  // Clear the loop-breaker flag whenever the user explicitly starts a login.
  const startLogin = (params: Parameters<typeof loginWithRedirect>[0]) => {
    sessionStorage.removeItem('previsit_forced_logout');
    // Deep-link-after-login: if ProtectedRoute stashed where the user was headed (e.g. a
    // /doctor-review/:taskId email link), thread it into Auth0 appState. Do NOT delete it here —
    // leave it in sessionStorage as a DURABLE fallback, because the mobile Auth0 +
    // device-verification multi-hop can drop appState; onRedirectCallback recovers from the key and
    // clears it after the landing.
    let returnTo: string | null = null;
    try {
      returnTo = sessionStorage.getItem('previsit_return_to');
    } catch { /* ignore */ }
    loginWithRedirect(isSafeReturnPath(returnTo) ? { ...params, appState: { returnTo } } : params);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#2A2A2A] to-[#161616] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white tracking-tight">RCMTask</h1>
          <p className="text-white/70 mt-2 text-sm">Medical Practice Task Management</p>
        </div>

        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <div className="text-center mb-6">
            <div className="w-12 h-12 bg-[#F4F1EE] rounded-xl flex items-center justify-center mx-auto mb-3">
              <Shield className="w-6 h-6 text-[#C94B1F]" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">Sign in to RCMTask</h2>
            <p className="text-sm text-gray-500 mt-1">Secure access for your medical practice</p>
          </div>

          {/* Show Auth0 errors visibly */}
          {authError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-red-700">{authError}</p>
            </div>
          )}

          <div className="space-y-3">
            <button
              onClick={() => startLogin({ authorizationParams: { connection: 'rekhatech-entra' } })}
              disabled={isLoading}
              className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors text-sm font-medium text-gray-700 disabled:opacity-50"
            >
              <svg className="w-5 h-5" viewBox="0 0 21 21">
                <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
                <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
                <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
                <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
              </svg>
              Continue with Microsoft
            </button>

            <button
              onClick={() => startLogin({ authorizationParams: { connection: 'google-oauth2' } })}
              disabled={isLoading}
              className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors text-sm font-medium text-gray-700 disabled:opacity-50"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              Continue with Google
            </button>

            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200" />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="bg-white px-3 text-gray-400">or</span>
              </div>
            </div>

            <button
              onClick={() => startLogin({
                authorizationParams: {
                  connection: 'Username-Password-Authentication',
                  prompt: 'login',
                }
              })}
              disabled={isLoading}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-[#C94B1F] text-white rounded-xl hover:bg-[#A8390F] transition-colors text-sm font-medium disabled:opacity-50"
            >
              {isLoading ? (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  <Building2 className="w-4 h-4" />
                  Sign in with Email &amp; Password
                </>
              )}
            </button>
          </div>

          <p className="text-center text-xs text-gray-400 mt-6">
            HIPAA compliant · AES-256 encrypted · Full audit trail
          </p>
        </div>

        <p className="text-center text-white/60 text-sm mt-6">
          New to RCMTask?{' '}
          <a href="/register" className="text-white underline underline-offset-2 hover:text-white/80">
            Start your free 30-day trial
          </a>
        </p>
      </div>
    </div>
  );
}
